import { ReactNode, CSSProperties } from "react";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { HeaderLogoTitleTagline } from "../app/headers/layout";
import { Avatar } from "../ui/Avatar";
import { cn } from "../../lib/utils";

function getRoomInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "";
  const words = trimmed.split(/\s+/);
  const first = words[0] ?? "";
  const second = words.length > 1 ? (words[1] ?? "") : "";
  return (first.charAt(0) + second.charAt(0)).toUpperCase();
}

export function RoomPageWrapper({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "flex flex-col bg-primary-yellow min-h-full [&_.safe-area-top-wrapper]:bg-white",
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}

export function RoomHeader({ room }: { room: PrivateRoom }) {
  // CONTENT language: this is the CURRENT room own header, so it follows the
  // in-room translate control rather than the interface locale.
  const { roomTextLang } = useActiveLang();
  const roomName = room === null ? "" : getRoomName(room, roomTextLang);
  const logoUrl = getRoomLogoUrl(room);

  return (
    <HeaderLogoTitleTagline
      logo={
        logoUrl.length > 0 ? (
          <Avatar
            variant="image"
            src={logoUrl}
            alt={roomName}
            imageFit="scale-down"
            size="lg"
            badge={null}
          />
        ) : (
          <Avatar
            variant="initials"
            initials={getRoomInitials(roomName)}
            size="lg"
            badge={null}
          />
        )
      }
      title={roomName}
      tagline={room.organisationModel.name}
    />
  );
}

export function RoomBlockHeading({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "m-[16px_0] p-[0_24px] flex items-center gap-1.5 text-primary-black font-dm-sans text-base font-bold leading-[110%] [&_svg_path]:size-5 ",
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}

export function RoomListWrapper({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "p-[0_8px_16px] flex flex-col divide-y divide-ds-slate-100 *:py-3 [&_.no-results]:text-center [&_.no-results]:text-[currentColor] [&_.no-results]:font-dm-sans [&_.no-results]:text-base",
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}

export function RoomResourcesTabsContainer({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center [&_.tab-switcher-container]:border-none [&_.tab-switcher-container]:rounded-2xl [&_.tab-switcher-container]:bg-white [&_.tab-switcher-button]:rounded-2xl [&_.tab-switcher-button]:text-primary-black [&_.tab-switcher-button]:text-center [&_.tab-switcher-button]:font-bold [&_.tab-switcher-button]:leading-normal [&_.tab-switcher-button.active]:bg-primary-yellow [&_.tab-switcher-button.active]:shadow-[1px_1px_10px_0px_rgba(33,33,33,0.1)]",
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}
